import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import chatbotImg from '../images/chatbot.png';
import chatbotImgAr from '../images/chatbot-ar.png';
import '../layouts/AppLayout.css'; // Use the new separated CSS
import NewsCarousel from '../components/NewsCarousel';
import { useTypingEffect } from '../hooks/useTypingEffect';
import SearchAutocomplete from '../components/SearchAutocomplete';

const LandingPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [showingSuggestions, setShowingSuggestions] = useState(false);
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    // Titles for the typing effect
    const typingTexts = useMemo(() => [
        t('landing.searchTitle', 'Recherche de documents juridiques algériens'),
        t('landing.searchTitleAlt', 'Chercher dans une base de +60k de contenus juridiques'),
        t('landing.searchTitleTest', 'Testez gratuitement notre plateforme dès maintenant'),
    ], [t]);

    // Use the typing effect hook
    const { displayText, hasStarted } = useTypingEffect({
        texts: typingTexts,
        typingSpeed: 60,
        deletingSpeed: 40,
        pauseBeforeDelete: 2000,
        pauseBeforeType: 2000,
        initialDelay: 5000,
    });

    // Custom hook for intersection observer
    const useIntersectionObserver = (options = {}) => {
        useEffect(() => {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target); // Only animate once
                    }
                });
            }, { threshold: 0.1, ...options });

            const targets = document.querySelectorAll('.animate-on-scroll');
            targets.forEach(target => observer.observe(target));

            return () => {
                targets.forEach(target => observer.unobserve(target));
                observer.disconnect();
            };
        }, [options]);
    };

    useIntersectionObserver();

    const handleSearch = () => {
        if (searchQuery.trim()) {
            navigate(`/${i18n.language}/search?q=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSearch();
    };

    // Dynamic chatbot image based on language
    const currentChatbotImg = i18n.language === 'ar' ? chatbotImgAr : chatbotImg;

    return (
        <div className="landing-content">
            {/* Zone Centrale - Focus sur la Recherche et l'IA */}
            <section className="hero-section fade-in-up">
                <div className="hero-content">
                    <h1 className="hero-title typing-title">
                        <i className="bi bi-search me-2"></i>
                        <span className="typing-text">{displayText}</span>
                        <span className="typing-cursor">|</span>
                    </h1>

                    {!localStorage.getItem('token') && (
                        <div className="d-flex justify-content-center mb-4">
                            <button
                                className="btn btn-warning btn-lg px-5 py-3 rounded-pill fw-bold shadow-sm animate-on-scroll fade-in-up"
                                style={{ fontSize: '1.2rem', transition: 'transform 0.2s' }}
                                onClick={() => navigate(`/${i18n.language}/register?plan=test`)}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <i className="bi bi-stars me-2"></i>
                                {t('landing.freeTrial', 'Test gratuit')}
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleFormSubmit} className="search-form-container">
                        <div className="search-bar-wrapper">
                            <SearchAutocomplete
                                globalMode={true}
                                value={searchQuery}
                                onChange={setSearchQuery}
                                onSubmit={handleSearch}
                                onShowSuggestionsChange={setShowingSuggestions}
                                onLoadingChange={setIsSearchLoading}
                                placeholder={t('landing.searchPlaceholder', 'Rechercher une loi, un article de code, un arrêt...')}
                            />
                            {!showingSuggestions && (
                                <button type="submit" className="search-button-large" disabled={isSearchLoading}>
                                    {isSearchLoading ? (
                                        <div className="button-spinner"></div>
                                    ) : (
                                        t('landing.searchButton', 'Rechercher')
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Recherche avancée button */}
                        <div className="d-flex justify-content-center mt-3">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm rounded-pill px-4 py-2 btn-light"
                                onClick={() => navigate(`/${i18n.language}/search`)}
                            >
                                <i className="bi bi-sliders me-2"></i>
                                {t('landing.advancedSearch', 'Recherche avancée')}
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            <div className="section-separator"></div>

            {/* JURI-BOT IA Section */}
            {/* JURI-BOT IA Section */}
            <section className="chatbot-section">
                <div className="chatbot-container">
                    <div className="chatbot-image-wrapper animate-on-scroll slide-in-left">
                        <img src={currentChatbotImg} alt="Chatbot Interface" className="chatbot-img" />
                    </div>
                    <div className="chatbot-text animate-on-scroll slide-in-right delay-200">
                        <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '30px' }}>
                            <i className="bi bi-robot me-2"></i> {t('landing.chatbotTitle', 'Juri-Bot IA')}
                        </h2>
                        <p className="chatbot-desc fw-bold">
                            {t('landing.chatbotDesc1', 'Obtenez des synthèses et des références instantanées.')}
                        </p>
                        <p className="chatbot-desc" style={{ marginBottom: '30px' }}>
                            {t('landing.chatbotDesc2', 'Posez vos questions en langage naturel.')}
                        </p>
                        <button
                            className="chatbot-start-btn"
                            onClick={() => navigate(`/${i18n.language}/chat`)}
                        >
                            {t('landing.startConversation', 'Démarrer la conversation')}
                        </button>
                    </div>
                </div>
            </section>

            <div className="section-separator"></div>

            {/* Actualités Juridiques Récentes */}
            <section className="news-section">
                <h2 className="section-title animate-on-scroll fade-in-up">
                    <i className="bi bi-newspaper me-2"></i> {t('landing.newsTitle', 'Actualités Juridiques Récentes')}
                </h2>
                <div className="news-container">
                    <NewsCarousel />
                </div>
            </section>
        </div>
    );
};

export default LandingPage;
